import { useEffect, useMemo, useRef, useState } from 'react';
import { useToast } from '@/components/common/toast';
import { FilterBar } from '@/components/common/FilterBar';
import { Select2 } from '@/components/forms/Select2';
import { Select2AsyncMulti } from '@/components/forms/Select2AsyncMulti';
import { AsyncButton } from '@/components/common/AsyncButton';
import { Modal } from '@/components/dialogs/Modal';
import { useServerDataTable } from '@/components/tables/useServerDataTable';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { ApiError } from '@/api/client/abpError';
import { swapFreeControlApi, makeLoginsByGroupsFetcher } from '@/api/services/swapFreeControl';
import {
  ConfigurationType,
  SwapCalculationType,
  SwapDirection,
  type CreateSwapFreeConfigurationFromTargetsInput,
  type GetGroupTargetsInput,
  type SwapFreeOptionsDto,
  type SwapFreeTargetDto,
} from '@/api/types/swapFreeControl';
import { CALC_LABELS, DIRECTION_LABELS } from '../helpers';

interface Props {
  options: SwapFreeOptionsDto;
  onSaved: () => void;
}

type SelectMode = 'some' | 'allMatched';

export function AddConfigTab({ options, onSaved }: Props) {
  const toast = useToast();

  // Settings form.
  const [name, setName] = useState('');
  const [days, setDays] = useState('1');
  const [direction, setDirection] = useState(String(SwapDirection.Normal));
  const [calc, setCalc] = useState(String(SwapCalculationType.PositionBased));

  // Target filters.
  const [scope, setScope] = useState<ConfigurationType>(ConfigurationType.Groups);
  const [filter, setFilter] = useState('');

  // Fetch state.
  const [hasFetched, setHasFetched] = useState(false);
  const [lastFetchedFilter, setLastFetchedFilter] = useState('');
  const [applied, setApplied] = useState<{ filter: string } | null>(null);

  // Selection state.
  const [mode, setMode] = useState<SelectMode>('some');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [excluded, setExcluded] = useState<Set<string>>(new Set()); // deselected when allMatched
  const [exceptionsByGroup, setExceptionsByGroup] = useState<Record<string, string[]>>({});
  const autoSelectedRef = useRef(false);

  const [saveOpen, setSaveOpen] = useState(false);

  const isGroups = scope === ConfigurationType.Groups;

  const extra = useMemo<GetGroupTargetsInput>(
    () => ({ filter: applied?.filter ?? '' }),
    [applied],
  );
  const table = useServerDataTable<SwapFreeTargetDto, GetGroupTargetsInput>({
    queryKey: ['swap-free', 'targets', scope],
    fetcher: (req) => (isGroups ? swapFreeControlApi.getGroupTargets(req) : swapFreeControlApi.getLoginTargets(req)),
    pageSize: 5,
    extraParams: extra,
    enabled: hasFetched && applied != null,
  });

  const matched = table.totalCount;

  // Groups: auto-select all matched once after the first page draws.
  useEffect(() => {
    if (!isGroups || !applied || autoSelectedRef.current || table.isFetching) return;
    if (table.rows.length === 0 && matched === 0) return;
    autoSelectedRef.current = true;
    setMode('allMatched');
    setExcluded(new Set());
    setPicked(new Set());
  }, [isGroups, applied, table.isFetching, table.rows.length, matched]);

  const resetResults = () => {
    setHasFetched(false);
    setApplied(null);
    setMode('some');
    setPicked(new Set());
    setExcluded(new Set());
    setExceptionsByGroup({});
    autoSelectedRef.current = false;
  };

  const doFetch = () => {
    const f = isGroups && !filter.trim() ? '*' : filter.trim();
    setLastFetchedFilter(f);
    setMode('some');
    setPicked(new Set());
    setExcluded(new Set());
    setExceptionsByGroup({});
    autoSelectedRef.current = false;
    table.setPageIndex(0);
    setHasFetched(true);
    setApplied({ filter: f });
  };

  const doClear = () => {
    setFilter('');
    resetResults();
  };

  const onScopeChange = (next: ConfigurationType) => {
    setScope(next);
    setFilter('');
    resetResults();
  };

  // Selection helpers -----------------------------------------------------------
  const isRowSelected = (target: string): boolean =>
    mode === 'allMatched' ? !excluded.has(target) : picked.has(target);

  const toggleRow = (target: string) => {
    if (mode === 'allMatched') {
      setExcluded((prev) => {
        const next = new Set(prev);
        if (next.has(target)) next.delete(target);
        else next.add(target);
        return next;
      });
    } else {
      setPicked((prev) => {
        const next = new Set(prev);
        if (next.has(target)) next.delete(target);
        else next.add(target);
        return next;
      });
    }
  };

  const allOnViewSelected = table.rows.length > 0 && table.rows.every((r) => isRowSelected(r.target));

  const toggleSelectAll = (checked: boolean) => {
    if (checked) {
      setMode('allMatched');
      setExcluded(new Set());
      setPicked(new Set());
    } else {
      setMode('some');
      setPicked(new Set());
      setExcluded(new Set());
    }
  };

  const selectedCount = mode === 'allMatched' ? Math.max(0, matched - excluded.size) : picked.size;

  const totalExcludedLogins = useMemo(
    () => new Set(Object.values(exceptionsByGroup).flat()).size,
    [exceptionsByGroup],
  );

  // Validation + save -----------------------------------------------------------
  const validate = (): boolean => {
    if (!name.trim()) {
      toast.error('Config Name is required.');
      return false;
    }
    if (!(Number(days) >= 0) || days.trim() === '') {
      toast.error('Swap-Free Days must be zero or greater.');
      return false;
    }
    if (selectedCount === 0) {
      toast.error('Select at least one target before saving.');
      return false;
    }
    return true;
  };

  const openSave = () => {
    if (validate()) setSaveOpen(true);
  };

  const buildPayload = (): CreateSwapFreeConfigurationFromTargetsInput => {
    const selectAll = mode === 'allMatched';
    const base: CreateSwapFreeConfigurationFromTargetsInput = {
      name: name.trim(),
      type: scope,
      swapFreeDays: Number(days),
      swapDirection: Number(direction),
      swapCalculationType: Number(calc),
      filter: lastFetchedFilter,
      selectAllMatched: selectAll,
      selectedGroups: [],
      deselectedGroups: [],
      loginNumbers: [],
      deselectedLogins: [],
      exceptionLogins: [],
    };
    if (isGroups) {
      base.selectedGroups = selectAll ? [] : [...picked];
      base.deselectedGroups = selectAll ? [...excluded] : [];
      const allExceptions = new Set<number>();
      Object.values(exceptionsByGroup).forEach((arr) => arr.forEach((l) => allExceptions.add(Number(l))));
      base.exceptionLogins = [...allExceptions];
    } else {
      base.loginNumbers = selectAll ? [] : [...picked].map((v) => Number(v));
      base.deselectedLogins = selectAll ? [...excluded].map((v) => Number(v)) : [];
    }
    return base;
  };

  const doSave = async () => {
    try {
      await swapFreeControlApi.createFromTargets(buildPayload());
      toast.success('Configuration saved successfully.');
      setSaveOpen(false);
      // Reset form + results.
      setName('');
      setDays('1');
      setDirection(String(SwapDirection.Normal));
      setCalc(String(SwapCalculationType.PositionBased));
      setFilter('');
      resetResults();
      onSaved();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to save configuration.');
    }
  };

  // Columns ---------------------------------------------------------------------
  const columns: DataGridColumn<SwapFreeTargetDto>[] = [
    {
      key: 'selected',
      sortable: false,
      hideable: false,
      filterable: false,
      className: 'text-center',
      headerClassName: 'text-center',
      title: (
        <input
          type="checkbox"
          className="form-check-input"
          checked={mode === 'allMatched' && excluded.size === 0}
          ref={(el) => {
            if (el) el.indeterminate = (mode === 'allMatched' && excluded.size > 0) || (mode === 'some' && picked.size > 0 && !allOnViewSelected);
          }}
          onChange={(e) => toggleSelectAll(e.target.checked)}
        />
      ),
      value: () => '',
      cell: (r) => (
        <input
          type="checkbox"
          className="form-check-input"
          checked={isRowSelected(r.target)}
          onChange={() => toggleRow(r.target)}
        />
      ),
    },
    {
      key: 'target',
      title: isGroups ? 'MT5 Group' : 'Login',
      className: 'text-start sfc-mono fw-semibold',
      value: (r) => r.target,
    },
  ];

  if (!isGroups) {
    columns.push({
      key: 'group',
      title: 'Group',
      className: 'text-start sfc-mono',
      value: (r) => r.group ?? '',
      cell: (r) => r.group ?? 'N/A',
    });
  } else {
    columns.push({
      key: 'loginCount',
      title: 'Logins',
      className: 'text-end',
      value: (r) => r.loginCount,
    });
    columns.push({
      key: 'exceptions',
      title: 'Exceptions',
      sortable: false,
      className: 'text-start',
      value: () => '',
      cell: (r) => {
        const fetch = makeLoginsByGroupsFetcher([r.target]);
        return (
          <div style={{ minWidth: 220 }}>
            <Select2AsyncMulti
              placeholder="Select Exceptions"
              pageSize={20}
              closeOnSelect={false}
              fetch={fetch}
              value={exceptionsByGroup[r.target] ?? []}
              onChange={(v) => setExceptionsByGroup((prev) => ({ ...prev, [r.target]: v }))}
            />
          </div>
        );
      },
    });
  }

  const directionLabel = DIRECTION_LABELS[Number(direction)] ?? '';
  const calcLabel = CALC_LABELS[Number(calc)] ?? '';
  const countLabel = isGroups
    ? `${selectedCount} of ${matched} groups selected${totalExcludedLogins ? ` · ${totalExcludedLogins} excluded` : ''}`
    : `${selectedCount} of ${matched} logins selected`;

  return (
    <div>
      {/* A. Settings */}
      <div className="mb-2">
        <i className="fe fe-settings me-2" />
        <span className="fw-semibold">Swap-Free Settings</span>
        <span className="text-muted ms-2 fs-12">
          New configurations are created inactive — activate them from the Configuration tab for swap-free to take effect.
        </span>
      </div>

      <div className="row g-2 align-items-end mb-3">
        <div className="col-lg-3 col-md-6">
          <label className="form-label">Config Name</label>
          <input className="form-control" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="col-lg-3 col-md-6">
          <label className="form-label" title="Swap-Free Days are calculated from the open time of the position.">Swap-Free Days</label>
          <input type="number" min="0" className="form-control" value={days} onChange={(e) => setDays(e.target.value)} />
        </div>
        <div className="col-lg-3 col-md-6">
          <label className="form-label">Swap Direction</label>
          <Select2 searchable={false} allowClear={false} value={direction} options={options.directions.map((d) => ({ value: d.value, label: d.text }))} onChange={(v) => setDirection(v ?? String(SwapDirection.Normal))} />
        </div>
        <div className="col-lg-3 col-md-6">
          <label className="form-label">Swap Calculation Type</label>
          <Select2 searchable={false} allowClear={false} value={calc} options={options.calculationTypes.map((c) => ({ value: c.value, label: c.text }))} onChange={(v) => setCalc(v ?? String(SwapCalculationType.PositionBased))} />
        </div>
      </div>

      {/* B. Target filters */}
      <FilterBar
        card={false}
        className="mb-3"
        fields={[
          <div key="scope">
            <label className="form-label d-block">Target Scope</label>
            <div className="d-flex gap-3 pt-1">
              <div className="form-check">
                <input className="form-check-input" type="radio" id="sfc-scope-groups" checked={isGroups} onChange={() => onScopeChange(ConfigurationType.Groups)} />
                <label className="form-check-label" htmlFor="sfc-scope-groups">Groups</label>
              </div>
              <div className="form-check">
                <input className="form-check-input" type="radio" id="sfc-scope-logins" checked={!isGroups} onChange={() => onScopeChange(ConfigurationType.Logins)} />
                <label className="form-check-label" htmlFor="sfc-scope-logins">Logins</label>
              </div>
            </div>
          </div>,
          <div key="filter">
            <label className="form-label" title={isGroups ? "Use mask sign '*' to include all and negation sign '!' to exclude groups." : undefined}>
              {isGroups ? 'Group Filter' : 'Login'}
            </label>
            <input
              className="form-control sfc-mono"
              autoComplete="off"
              placeholder={isGroups ? 'e.g. real\\AB-*, !*Islamic*' : 'Login'}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && doFetch()}
            />
          </div>,
        ]}
        actions={
          <>
            <AsyncButton className="btn btn-primary btn-sm" icon="fa fa-search" loading={table.isFetching} onClick={doFetch}>Fetch</AsyncButton>
            <button type="button" className="btn btn-light btn-sm" onClick={doClear}>Clear</button>
          </>
        }
      />

      {/* C. Targets grid (only after Fetch) */}
      {hasFetched && (
        <DataGrid
          columns={columns}
          data={table.rows}
          rowKey={(r) => r.target}
          card
          columnVisibilityToggle={false}
          rowClassName={(r) => (isRowSelected(r.target) ? 'sfc-picked' : undefined)}
          toolbarStart={
            <div className="d-flex align-items-center gap-2">
              <span className="small text-muted">{countLabel}</span>
              <AsyncButton className="btn btn-primary btn-sm" icon="fa fa-save" onClick={openSave} disabled={selectedCount === 0}>Save Config</AsyncButton>
            </div>
          }
          emptyMessage={table.isLoading ? 'Loading…' : 'Could not load targets. Please try again.'}
          server={{
            pageIndex: table.pageIndex,
            pageSize: table.pageSize,
            pageCount: table.pageCount,
            totalCount: table.totalCount,
            onPageChange: table.setPageIndex,
            onPageSizeChange: table.setPageSize,
          }}
        />
      )}

      {/* Save confirm */}
      <Modal
        show={saveOpen}
        title="Save Configuration"
        onClose={() => setSaveOpen(false)}
        footer={
          <>
            <button type="button" className="btn btn-light btn-sm" onClick={() => setSaveOpen(false)}>Cancel</button>
            <AsyncButton className="btn btn-primary btn-sm" onClick={doSave}>Save</AsyncButton>
          </>
        }
      >
        <table className="table table-sm mb-0">
          <tbody>
            <tr><th>Config Name</th><td>{name.trim()}</td></tr>
            <tr><th>Target Scope</th><td>{isGroups ? 'Groups' : 'Logins'}</td></tr>
            <tr><th>Targets</th><td>{selectedCount}</td></tr>
            <tr><th>Swap-Free Days</th><td>{days}</td></tr>
            <tr><th>Swap Direction</th><td>{directionLabel}</td></tr>
            <tr><th>Calculation Type</th><td>{calcLabel}</td></tr>
            {isGroups && <tr><th>Excluded Logins</th><td>{totalExcludedLogins}</td></tr>}
            <tr><th>Swap-Free Status</th><td>InActive</td></tr>
          </tbody>
        </table>
      </Modal>
    </div>
  );
}
